import { useEffect, useRef, type RefObject } from 'react';
import { Platform } from 'react-native';

/**
 * useWebOutsidePress — on web, call `onOutside` when a pointer goes down
 * outside the element behind `ref` while `active` is true. No-op on native.
 *
 * For tap-toggled dropdowns that have no hover to close them (the compact
 * header's profile menu, CORE-311). The press is not swallowed: whatever was
 * tapped outside still gets it, like any web dropdown.
 */
export function useWebOutsidePress(ref: RefObject<unknown>, active: boolean, onOutside: () => void): void {
  const onOutsideRef = useRef(onOutside);
  onOutsideRef.current = onOutside;

  useEffect(() => {
    if (Platform.OS !== 'web' || !active || typeof document === 'undefined') return;
    const onPointerDown = (e: Event) => {
      const node = ref.current as { contains?: (n: Node) => boolean } | null;
      if (node?.contains && e.target instanceof Node && node.contains(e.target)) return;
      onOutsideRef.current();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [ref, active]);
}
